import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { FormField, Input, ZagButton, AppStatusComponent } from '@zag/ui';
import { AuthService } from './auth.service';

/**
 * Custom validator to check that the new password and its confirmation match.
 */
function passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
  const newPassword = control.get('newPassword');
  const confirmPassword = control.get('confirmPassword');

  if (!newPassword || !confirmPassword) {
    return null;
  }

  return newPassword.value === confirmPassword.value ? null : { passwordMismatch: true };
}

/**
 * Reset-password page reached from the emailed link.
 *
 * Reads the reset token from the `token` query param, lets the user set a new
 * password, and on success redirects to `/login`. A missing token short-circuits
 * to an error state; an invalid/expired token surfaces the auth-service error.
 */
@Component({
  selector: 'sc-reset-password',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, ReactiveFormsModule, TranslatePipe, FormField, Input, ZagButton, AppStatusComponent],
  template: `
    <div class="auth-container">
      <div class="auth-form">
        <h2 class="auth-header">
          <img src="/logo.svg" alt="Logo" />
          <span>{{ 'auth.resetPassword.title' | translate }}</span>
        </h2>

        @if (!token()) {
          <div class="error-message" role="alert">{{ 'auth.resetPassword.missingToken' | translate }}</div>
        } @else {
          @if (authService.error()) {
            <div class="error-message" role="alert">{{ authService.error() }}</div>
          }

          <p class="auth-description">{{ 'auth.resetPassword.description' | translate }}</p>

          <form [formGroup]="resetForm" (ngSubmit)="onSubmit()" method="post">
            <div class="form-fields">
              <zag-form-field
                [label]="'auth.resetPassword.newPassword' | translate"
                [error]="
                  resetForm.get('newPassword')?.touched && resetForm.get('newPassword')?.invalid
                    ? ('errors.passwordMinLength' | translate)
                    : ''
                "
                [required]="true"
                forId="reset-password"
              >
                <zag-input
                  id="reset-password"
                  formControlName="newPassword"
                  type="password"
                  [placeholder]="'auth.resetPassword.newPasswordPlaceholder' | translate"
                ></zag-input>
              </zag-form-field>

              <zag-form-field
                [label]="'auth.resetPassword.confirmPassword' | translate"
                [error]="getConfirmPasswordError() | translate"
                [required]="true"
                forId="reset-confirm-password"
              >
                <zag-input
                  id="reset-confirm-password"
                  formControlName="confirmPassword"
                  type="password"
                  [placeholder]="'auth.resetPassword.confirmPasswordPlaceholder' | translate"
                ></zag-input>
              </zag-form-field>

              <button zagButton zagType="primary" type="submit" [disabled]="authService.isLoading()" class="submit-button">
                {{ 'auth.resetPassword.submit' | translate }}
              </button>
            </div>
          </form>
        }

        <div class="auth-footer">
          <a routerLink="/login" class="link">{{ 'auth.resetPassword.backToLogin' | translate }}</a>
        </div>
      </div>
      <footer class="auth-status-footer">
        <zag-app-status />
      </footer>
    </div>
  `,
  styles: [
    `
      .auth-container {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: var(--spacing-md);
        height: 100dvh;
        box-sizing: border-box;
        overflow: hidden;
      }

      .auth-status-footer {
        position: absolute;
        bottom: var(--spacing-md);
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        pointer-events: none;
      }

      .auth-form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
        width: 100%;
      }

      .form-fields {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .auth-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin-bottom: var(--spacing-md);
      }

      .auth-header img {
        width: 92px;
        height: auto;
      }

      .auth-description {
        margin: 0;
        color: var(--color-neutral-2);
        font-size: var(--font-size-sm);
      }

      .error-message {
        padding: var(--spacing-sm) var(--spacing-md);
        background-color: var(--color-error-bg);
        border: 1px solid var(--color-error-border);
        border-radius: var(--border-radius-sm);
        color: var(--color-error);
        font-size: var(--font-size-sm);
      }

      .auth-footer {
        text-align: center;
        margin-top: var(--spacing-md);
      }

      .link {
        color: var(--color-primary-alt);
        text-decoration: none;
        font-weight: var(--font-weight-medium);
      }

      .link:hover {
        text-decoration: underline;
      }
    `,
  ],
})
export class ResetPasswordComponent {
  protected readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly token = signal<string | null>(this.route.snapshot.queryParamMap.get('token'));

  readonly resetForm = new FormGroup(
    {
      newPassword: new FormControl('', [Validators.required, Validators.minLength(8)]),
      confirmPassword: new FormControl('', [Validators.required]),
    },
    { validators: passwordsMatchValidator }
  );

  getConfirmPasswordError(): string {
    const control = this.resetForm.get('confirmPassword');

    if (!control?.touched) {
      return '';
    }

    if (control.hasError('required')) {
      return 'errors.required';
    }

    if (this.resetForm.hasError('passwordMismatch')) {
      return 'errors.passwordMismatch';
    }

    return '';
  }

  async onSubmit(): Promise<void> {
    const token = this.token();
    if (!token) {
      return;
    }

    if (!this.resetForm.valid) {
      this.resetForm.markAllAsTouched();
      return;
    }

    this.authService.clearError();
    const newPassword = this.resetForm.value.newPassword;
    if (!newPassword) {
      return;
    }

    try {
      await this.authService.resetPassword(token, newPassword);
      await this.router.navigate(['/login'], { queryParams: { reset: 'success' } });
    } catch {
      // Error message is surfaced via authService.error()
    }
  }
}
